import { Link, createFileRoute } from '@tanstack/react-router';
import { useState, type ComponentType } from 'react';
import {
  ArrowRight,
  BarChart3,
  Boxes,
  CircleCheck,
  FileText,
  Landmark,
  Receipt,
  Repeat,
  Sparkles,
  Store,
} from 'lucide-react';
import { PRODUCT_NAME } from '@/components/brand/logo';
import { TryTheDemo } from '@/components/demo-chooser';
import { ProductPreview } from '@/components/marketing/product-preview';
import { SiteFooter, SiteHeader } from '@/components/marketing/site-chrome';
import { WorldSection } from '@/components/marketing/world-section';
import { Button } from '@/components/ui/button';
import { auth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/')({ component: HomePage });

type Icon = ComponentType<{ className?: string }>;

/** The product families, listed editorially beside the product preview. */
const PRODUCTS: Array<{ icon: Icon; title: string; body: string }> = [
  {
    icon: FileText,
    title: 'Invoicing and sales',
    body: 'Quotes, invoices, credit notes and payments, with recurring invoices and branded PDFs.',
  },
  {
    icon: Receipt,
    title: 'Expenses and bills',
    body: 'Record expenses, pay vendor bills and raise purchase orders before you buy.',
  },
  {
    icon: Landmark,
    title: 'Banking',
    body: 'Import statements, categorise with rules and reconcile every account to the rupee.',
  },
  {
    icon: Store,
    title: 'GST and e-invoicing',
    body: 'GST worked out on every line, monthly summaries ready for filing, e-invoices on demand.',
  },
  {
    icon: BarChart3,
    title: 'Reports',
    body: 'Profit and loss, balance sheet, trial balance and ageing, by class, location or project.',
  },
  {
    icon: Boxes,
    title: 'Inventory',
    body: 'Stock on hand with FIFO costing, so cost of goods sold is right every month.',
  },
  {
    icon: Sparkles,
    title: 'AI Accountant',
    body: 'Drop in a bank statement or a bill; it reads each line and suggests how to book it.',
  },
  {
    icon: Repeat,
    title: 'QuickBooks migration',
    body: 'Bring lists, balances and history across, then compare month by month until they match.',
  },
];

/** The kinds of business in the photo strip (photos: public/photos, credits in CREDITS.md). */
const TRADES = [
  {
    src: '/photos/cafe-owner.webp',
    label: 'Cafés',
    alt: 'A café owner seated in his café',
  },
  {
    src: '/photos/restaurant-owner.webp',
    label: 'Restaurants',
    alt: 'A restaurant owner standing in his dining room',
  },
  {
    src: '/photos/store-owner.webp',
    label: 'Retail stores',
    alt: 'A store owner with arms crossed among his shelves',
  },
  {
    src: '/photos/studio-owner.webp',
    label: 'Studios and services',
    alt: 'A business owner at her office desk with a laptop',
  },
];

/** "How it works" tabs; each has a small sample screen. */
const FLOWS: Array<{ key: string; label: string; title: string; body: string; points: string[] }> =
  [
    {
      key: 'sell',
      label: 'Get paid',
      title: 'Send an invoice in a minute and see when it is paid',
      body: 'Create quotes and invoices with GST worked out for you, email them from Yedo Books and record payments as they arrive.',
      points: [
        'Quotes that turn into invoices in one click',
        'Recurring invoices for retainers and rent',
        'Ageing shows who owes what, and for how long',
      ],
    },
    {
      key: 'spend',
      label: 'Control spend',
      title: 'Every rupee out, approved and accounted for',
      body: 'Raise purchase orders, turn them into bills and pay vendors on time. Expenses land in the right account with the right tax.',
      points: [
        'Purchase orders that become bills',
        'Vendor balances and bills due at a glance',
        'Receipts and bills read for you by the AI Accountant',
      ],
    },
    {
      key: 'bank',
      label: 'Reconcile',
      title: 'Bank lines that match themselves',
      body: 'Import a statement, let rules and matches do the routine work, and reconcile to the closing balance with confidence.',
      points: [
        'Rules that categorise repeat transactions',
        'Matches against open invoices and bills',
        'A reconcile screen that shows the difference until it is zero',
      ],
    },
    {
      key: 'close',
      label: 'Close the month',
      title: 'Books your accountant can sign off',
      body: 'Lock periods once they are reviewed, keep a full audit log and run the reports that matter, for one business or many.',
      points: [
        'Period close with a full audit trail',
        'Reports by class, location and project',
        'One place for the accountant and the business owner',
      ],
    },
  ];

function HomePage() {
  return (
    <div className="min-h-screen bg-brand-paper text-brand-navy">
      <SiteHeader />
      <main>
        <Hero />
        <Trades />
        <Products />
        <HowItWorks />
        <Audiences />
        <WorldSection />
        <IndiaReady />
        <CallToAction />
      </main>
      <SiteFooter />
    </div>
  );
}

function Eyebrow({ children, onDark }: { children: string; onDark?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 text-xs font-semibold tracking-[0.16em] uppercase',
        onDark ? 'text-brand-blue-200' : 'text-primary',
      )}
    >
      <span className={cn('h-px w-8', onDark ? 'bg-brand-blue-200' : 'bg-primary')} />
      {children}
    </div>
  );
}

/** Staggers an entrance animation (motion-safe only, see index.css). */
const after = (ms: number) => ({ animationDelay: `${ms}ms` });

function Hero() {
  return (
    <section className="bg-ledger relative overflow-hidden border-b">
      <div className="relative mx-auto max-w-7xl px-6 pt-14 pb-10 text-center">
        <span
          style={after(0)}
          className="motion-safe:animate-rise inline-flex items-center gap-2 rounded-full border border-brand-blue-100 bg-white px-3 py-1 text-xs font-semibold text-primary"
        >
          <Sparkles className="size-3.5" /> Now with the AI Accountant
        </span>
        <h1
          style={after(90)}
          className="motion-safe:animate-rise mx-auto mt-6 max-w-5xl text-4xl font-bold leading-[1.08] tracking-[-0.03em] sm:text-5xl lg:text-[3.5rem]"
        >
          The books your business runs on,
          <br className="hidden sm:block" />{' '}
          <span className="bg-gradient-to-r from-primary to-brand-purple bg-clip-text text-transparent">
            in one place
          </span>
        </h1>
        <p
          style={after(180)}
          className="motion-safe:animate-rise mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-brand-slate"
        >
          {PRODUCT_NAME} brings invoicing, expenses, banking, GST and reports together, so business
          owners and their accountants work from the same numbers, every day.
        </p>
        <div
          style={after(270)}
          className="motion-safe:animate-rise mt-8 flex flex-wrap items-center justify-center gap-3"
        >
          <Button asChild size="lg" className="group h-12 px-6 text-base font-semibold">
            <Link to="/signup">
              Get started{' '}
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Button>
          {/* the public demo when this site has one (ADR-0020); Log in stays in the header */}
          {auth.demo ? (
            <TryTheDemo className="h-12 px-6 text-base font-semibold text-brand-navy" />
          ) : (
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 px-6 text-base font-semibold text-brand-navy"
            >
              <Link to="/login">Log in</Link>
            </Button>
          )}
        </div>
        <div style={after(360)} className="motion-safe:animate-rise mx-auto mt-12 max-w-5xl">
          <ProductPreview />
        </div>
      </div>
    </section>
  );
}

function Trades() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Eyebrow>Who it is for</Eyebrow>
            <h2 className="mt-4 font-display text-4xl font-medium tracking-[-0.01em]">
              Made for growing businesses
            </h2>
          </div>
          <p className="max-w-md text-brand-slate">
            Cafés, restaurants, stores and studios with a team, suppliers and an accountant to keep
            happy.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 items-start gap-3 sm:gap-4 lg:grid-cols-4">
          {TRADES.map((t, i) => (
            <figure
              key={t.label}
              className={cn(
                'group relative overflow-hidden rounded-2xl',
                i % 2 === 1 ? 'lg:mt-10' : '',
              )}
            >
              <img
                src={t.src}
                alt={t.alt}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover transition duration-500 group-hover:scale-[1.03]"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 pt-16 text-sm font-semibold text-white">
                {t.label}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function Products() {
  return (
    <section id="products" className="bg-dots scroll-mt-16 border-y py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <Eyebrow>Products</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-medium tracking-[-0.01em]">
            Everything your books need, in one ledger
          </h2>
          <p className="mt-4 max-w-lg text-lg leading-relaxed text-brand-slate">
            Start with invoicing and banking. Turn on inventory, projects and e-invoicing when you
            need them; it is all the same ledger underneath.
          </p>
        </div>
        <ul className="mt-12 grid gap-x-10 sm:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map((p, i) => (
            <li key={p.title} className="border-t border-brand-navy/15 py-6">
              <div className="flex items-center gap-3">
                <span className="font-display text-sm text-primary tnum">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p.icon className="size-4 text-primary" />
                <h3 className="font-semibold">{p.title}</h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-brand-slate">{p.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function HowItWorks() {
  const [active, setActive] = useState(FLOWS[0]!.key);
  const flow = FLOWS.find((f) => f.key === active)!;
  return (
    <section id="how-it-works" className="scroll-mt-16 bg-white py-24">
      <div className="mx-auto max-w-7xl px-6">
        <Eyebrow>How it works</Eyebrow>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.01em]">
          From the first invoice to the month-end close
        </h2>
        <div
          role="tablist"
          aria-label="How it works"
          className="mt-10 flex flex-wrap gap-8 border-b"
        >
          {FLOWS.map((f, i) => (
            <button
              key={f.key}
              role="tab"
              type="button"
              aria-selected={f.key === active}
              onClick={() => setActive(f.key)}
              className={cn(
                '-mb-px flex items-baseline gap-2 border-b-2 pb-3 text-base font-semibold transition',
                f.key === active
                  ? 'border-primary text-brand-navy'
                  : 'border-transparent text-brand-slate hover:text-brand-navy',
              )}
            >
              <span className="font-display text-sm text-primary">0{i + 1}</span>
              {f.label}
            </button>
          ))}
        </div>
        <div
          key={flow.key}
          role="tabpanel"
          className="motion-safe:animate-fade mt-10 grid items-center gap-12 md:grid-cols-2"
        >
          <div>
            <h3 className="font-display text-3xl font-medium">{flow.title}</h3>
            <p className="mt-4 leading-relaxed text-brand-slate">{flow.body}</p>
            <ul className="mt-6 space-y-3">
              {flow.points.map((pt) => (
                <li key={pt} className="flex items-start gap-3 text-sm">
                  <CircleCheck className="mt-0.5 size-4 shrink-0 text-success" />
                  {pt}
                </li>
              ))}
            </ul>
          </div>
          <div aria-hidden="true" className="bg-ledger rounded-2xl border p-8 pl-24">
            {flow.key === 'sell' ? <InvoiceVisual /> : null}
            {flow.key === 'spend' ? <PurchaseVisual /> : null}
            {flow.key === 'bank' ? <BankVisual /> : null}
            {flow.key === 'close' ? <CloseVisual /> : null}
          </div>
        </div>
      </div>
    </section>
  );
}

const Chip = ({ tone, children }: { tone: string; children: string }) => (
  <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-bold uppercase', tone)}>
    {children}
  </span>
);
const GREEN = 'bg-[#e3f4ea] text-success';
const BLUE = 'bg-brand-blue-50 text-brand-blue-700';

function Row({ l, r, strong }: { l: string; r: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between py-1 text-xs', strong && 'text-sm font-bold')}>
      <span className={strong ? '' : 'text-brand-slate'}>{l}</span>
      <span className="tnum">{r}</span>
    </div>
  );
}

function InvoiceVisual() {
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold tracking-wider text-brand-slate uppercase">
            Invoice INV-1042
          </div>
          <div className="mt-1 font-semibold">Kaveri Foods Pvt Ltd</div>
        </div>
        <Chip tone={GREEN}>Paid</Chip>
      </div>
      <div className="mt-4 border-t pt-3">
        <Row l="Catering supplies × 40" r="₹1,20,000.00" />
        <Row l="Delivery" r="₹5,000.00" />
        <Row l="CGST 9%" r="₹11,250.00" />
        <Row l="SGST 9%" r="₹11,250.00" />
      </div>
      <div className="mt-2 border-t pt-2">
        <Row l="Total" r="₹1,47,500.00" strong />
      </div>
    </div>
  );
}

function PurchaseVisual() {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 rounded-lg bg-white p-4 shadow-sm">
        <div className="text-[11px] font-semibold tracking-wider text-brand-slate uppercase">
          Purchase order
        </div>
        <div className="mt-1 text-sm font-semibold">PO-0031</div>
        <div className="mt-1 text-xs text-brand-slate">Sharma Packaging</div>
        <div className="mt-3 text-sm font-bold tnum">₹64,900.00</div>
        <div className="mt-2">
          <Chip tone={GREEN}>Approved</Chip>
        </div>
      </div>
      <ArrowRight className="size-5 shrink-0 text-primary" />
      <div className="flex-1 rounded-lg bg-white p-4 shadow-sm ring-2 ring-primary">
        <div className="text-[11px] font-semibold tracking-wider text-brand-slate uppercase">
          Bill
        </div>
        <div className="mt-1 text-sm font-semibold">From PO-0031</div>
        <div className="mt-1 text-xs text-brand-slate">Due in 14 days</div>
        <div className="mt-3 text-sm font-bold tnum">₹64,900.00</div>
        <div className="mt-2">
          <Chip tone={BLUE}>Scheduled</Chip>
        </div>
      </div>
    </div>
  );
}

function BankVisual() {
  const lines = [
    { d: 'NEFT KAVERI FOODS', a: '+₹1,47,500.00', m: 'Invoice INV-1042', ok: true },
    { d: 'UPI AIRTEL BROADBAND', a: '−₹1,179.00', m: 'Rule: Internet', ok: true },
    { d: 'IMPS SHARMA PACKAGING', a: '−₹64,900.00', m: 'Bill from PO-0031', ok: true },
    { d: 'CASH DEPOSIT', a: '+₹20,000.00', m: 'Needs a category', ok: false },
  ];
  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm">
      <div className="flex items-center justify-between border-b px-4 py-2.5 text-xs font-semibold">
        <span>HDFC Current · Bank transactions</span>
        <Chip tone={BLUE}>3 of 4 matched</Chip>
      </div>
      {lines.map((l) => (
        <div key={l.d} className="flex items-center gap-3 border-b px-4 py-2.5 last:border-0">
          <CircleCheck className={cn('size-4 shrink-0', l.ok ? 'text-success' : 'text-border')} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium">{l.d}</div>
            <div className={cn('text-[11px]', l.ok ? 'text-brand-slate' : 'text-warning')}>
              {l.m}
            </div>
          </div>
          <span className="text-xs font-semibold tnum">{l.a}</span>
        </div>
      ))}
    </div>
  );
}

function CloseVisual() {
  const items = [
    { t: 'Bank accounts reconciled', s: 'Done' },
    { t: 'GST summary reviewed', s: 'Done' },
    { t: 'Accountant sign-off', s: 'Done' },
    { t: 'Month locked', s: 'Locked' },
  ];
  return (
    <div className="rounded-lg bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="font-semibold">Month-end close</div>
        <span className="text-xs font-semibold text-success">4 of 4</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-[#e3f4ea]">
        <div className="h-2 w-full rounded-full bg-brand-green" />
      </div>
      <ul className="mt-4 space-y-2.5">
        {items.map((i) => (
          <li key={i.t} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <CircleCheck className="size-4 text-success" />
              {i.t}
            </span>
            <Chip tone={i.s === 'Locked' ? BLUE : GREEN}>{i.s}</Chip>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Audiences() {
  const cards = [
    {
      img: '/photos/cafe-owner-greens.webp',
      alt: 'A café owner smiling in his café',
      title: 'For business owners',
      body: 'See cash, sales and what is owed on one screen. Send invoices, approve bills and answer your accountant without email chains.',
      points: [
        'The numbers that matter, on one dashboard',
        'Approvals and a document inbox',
        'Works the way QuickBooks users expect',
      ],
    },
    {
      img: '/photos/accountant-laptop.webp',
      alt: 'An accountant working through client books on a laptop',
      title: 'For accountants and practices',
      body: 'Run every client from one login. Switch clients in a click, close periods, and hand over reports your clients can read.',
      points: [
        'Client list with one-click switching',
        'Accountant tools, audit log and period close',
        'Migration from QuickBooks with a parallel run',
      ],
    },
  ];
  return (
    <section id="audiences" className="bg-dots scroll-mt-16 border-y py-24">
      <div className="mx-auto max-w-7xl px-6">
        <Eyebrow>Owners and accountants</Eyebrow>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium tracking-[-0.01em]">
          One ledger, two points of view
        </h2>
        <div className="mt-12 grid gap-8 md:grid-cols-2">
          {cards.map((c) => (
            <article key={c.title} className="overflow-hidden rounded-2xl border bg-white">
              <img
                src={c.img}
                alt={c.alt}
                loading="lazy"
                className="aspect-[16/8] w-full object-cover"
              />
              <div className="p-8">
                <h3 className="font-display text-2xl font-medium">{c.title}</h3>
                <p className="mt-3 leading-relaxed text-brand-slate">{c.body}</p>
                <ul className="mt-6 space-y-3">
                  {c.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3 text-sm">
                      <CircleCheck className="mt-0.5 size-4 shrink-0 text-primary" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function IndiaReady() {
  const facts = [
    { k: 'GST', v: 'CGST, SGST and IGST worked out from the place of supply' },
    { k: '₹', v: 'Amounts in lakhs and crores, as your team reads them' },
    { k: 'HSN/SAC', v: 'HSN and SAC codes on products and invoice lines' },
    { k: 'e-Invoice', v: 'E-invoicing when your turnover calls for it' },
  ];
  return (
    <section id="india" className="bg-jaali scroll-mt-16 bg-brand-blue-900 text-white">
      <div className="grid items-stretch lg:grid-cols-[1fr_1.15fr]">
        <div className="relative min-h-[320px] overflow-hidden">
          <img
            src="/photos/cafe-interior.webp"
            alt="A bright café with wooden tables by tall windows"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <div className="max-w-2xl px-6 py-20 lg:px-16">
          <Eyebrow onDark>India-ready</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-medium tracking-[-0.01em]">
            Made for how Indian businesses keep books
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-white/75">
            GST, rupees and e-invoicing are set up from the start. Working with a client abroad?
            Other countries and currencies are a setting away.
          </p>
          <dl className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2">
            {facts.map((f) => (
              <div key={f.k} className="border-t border-white/20 pt-4">
                <dt className="font-display text-2xl text-brand-blue-200">{f.k}</dt>
                <dd className="mt-1 text-sm leading-relaxed text-white/80">{f.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

function CallToAction() {
  return (
    <section className="relative overflow-hidden">
      <img
        src="/photos/coffee-shop-evening.webp"
        alt=""
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-navy-2/95 via-brand-navy-2/85 to-brand-navy-2/40" />
      <div className="relative mx-auto max-w-7xl px-6 py-28 text-white">
        <h2 className="max-w-2xl font-display text-5xl leading-tight font-medium tracking-[-0.01em]">
          Bring your books to {PRODUCT_NAME}
        </h2>
        <p className="mt-5 max-w-xl text-lg text-white/80">
          Create your account, invite your accountant and start with today's numbers.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Button
            asChild
            size="lg"
            className="h-12 rounded-full bg-white px-7 text-base font-semibold text-primary hover:bg-brand-blue-50"
          >
            <Link to="/signup">
              Sign up <ArrowRight className="size-4" />
            </Link>
          </Button>
          {auth.demo ? (
            <TryTheDemo className="h-12 rounded-full border-white/50 bg-transparent px-7 text-base font-semibold text-white hover:bg-white/10 hover:text-white" />
          ) : (
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-12 rounded-full border-white/50 bg-transparent px-7 text-base font-semibold text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/login">Log in</Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
